import React, { useState } from "react";
import { useVenue } from "../context/VenueContext";
import { useAuth } from "../context/AuthContext";
import { sendConnectionRequest } from "../services/userService";

export default function VenueUsersScreen({ setScreen, onOpenChat, onOpenProfile }) {
  const { activeUsers, venue } = useVenue();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [sent, setSent] = useState({});

  const filtered = activeUsers.filter(u =>
    u.uid !== user?.uid &&
    u.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  const handleConnect = async (otherUser) => {
    if (!user || user.isGuest) return;
    await sendConnectionRequest(user.uid, otherUser.uid);
    setSent(prev => ({ ...prev, [otherUser.uid]: true }));
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .vu-header { padding: 80px 40px 40px; position: relative; overflow: hidden; max-width: 900px; margin: 0 auto; text-align: center; }
        .vu-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 30%, rgba(29,185,84,0.08) 0%, transparent 60%); pointer-events: none; }
        .vu-title { font-family: 'DM Serif Display', serif; font-size: clamp(32px, 4vw, 48px); color: #ffffff; letter-spacing: -2px; position: relative; z-index: 1; }
        .vu-sub { font-size: 12px; color: rgba(255,255,255,0.5); margin-top: 4px; position: relative; z-index: 1; line-height: 1.5; }
        .vu-search { margin: 16px auto 0; position: relative; max-width: 720px; width: calc(100% - 80px); }
        .vu-search-input { width: 100%; padding: 14px 16px 14px 40px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); font-family: 'DM Sans', sans-serif; font-size: 14px; color: #ffffff; outline: none; transition: all 0.2s; }
        .vu-search-input:focus { border-color: rgba(255,255,255,0.3); }
        .vu-search-input::placeholder { color: rgba(255,255,255,0.3); }
        .vu-search-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.3); font-size: 14px; }
        .vu-list { padding: 24px 40px; display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 12px; max-width: 1100px; margin: 0 auto; }
        .vu-card { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 20px; cursor: pointer; transition: all 0.2s; }
        .vu-card:active { background: rgba(255,255,255,0.06); transform: scale(0.99); }
        .vu-avatar { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; flex-shrink: 0; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 18px; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); }
        .vu-name { font-size: 14px; font-weight: 600; color: #ffffff; letter-spacing: -0.3px; }
        .vu-meta { font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 2px; }
        .vu-actions { display: flex; gap: 6px; margin-left: auto; flex-shrink: 0; }
        .vu-btn { padding: 8px 14px; border-radius: 100px; border: none; font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .vu-btn:active { transform: scale(0.95); }
        .vu-btn-connect { background: #ffffff; color: #0A0A0A; font-weight: 700; }
        .vu-btn-msg { background: rgba(255,255,255,0.06); color: #ffffff; border: 1px solid rgba(255,255,255,0.15); }
        .vu-btn-msg:hover { background: rgba(255,255,255,0.1); }
        .vu-btn-sent { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.4); cursor: default; }
        .vu-empty { padding: 60px 20px; text-align: center; }
      `}</style>

      <div className="vu-header">
        <div className="vu-header-bg" />
        <div className="vu-title">Who's Here</div>
        <div className="vu-sub">{filtered.length} public profile{filtered.length !== 1 ? "s" : ""} at {venue?.name || "this venue"}</div>
      </div>

      <div className="vu-search">
        <span className="vu-search-icon">🔍</span>
        <input
          className="vu-search-input"
          placeholder="Search people here..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="vu-list">
        {filtered.length === 0 ? (
          <div className="vu-empty">
            <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 6, letterSpacing: -0.3 }}>No public profiles yet</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>
              {search ? "Try a different name" : "Users with public profiles will appear here"}
            </div>
          </div>
        ) : (
          filtered.map(u => (
            <div key={u.uid} className="vu-card" onClick={() => onOpenProfile(u.uid)}>
              {u.photoURL
                ? <img src={u.photoURL} alt="" className="vu-avatar" />
                : <div className="vu-avatar">👤</div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="vu-name">{u.displayName || "User"}</div>
                <div className="vu-meta">Active now · Public profile</div>
              </div>
              <div className="vu-actions" onClick={e => e.stopPropagation()}>
                {sent[u.uid] ? (
                  <span className="vu-btn vu-btn-sent">Sent</span>
                ) : (
                  <button className="vu-btn vu-btn-connect" onClick={() => handleConnect(u)}>Connect</button>
                )}
                <button className="vu-btn vu-btn-msg" onClick={() => onOpenChat(u)}>💬</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
